/* eslint-disable no-unused-vars */
import {
  ISwapSecurity,
  SwapSecurityField,
} from "@/lib/interfaces and Enums/ENUMSwapSecurity";
import {
  DEFAULT_SEARCH_LIMIT,
  ISearchResult,
  findItemByFieldValue,
} from "@/lib/referenceDataSearchHelper";
import { loadReferenceDataOnce } from "./referenceDataLoadHelper";
import swapSecuritiesStore from "./swapSecuritiesStore";

// LQC-2939 - GET /Securities/swaps, fetched once when a Swap or Swaption ticket opens.

export const SWAP_SECURITIES_ENDPOINT = "/Securities/swaps";

// ----- Pure transform -------------------------------------------------------

export function extractSwapSecurities(response: any): ISwapSecurity[] | null {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.swaps)) return response.swaps;
  return null;
}

// ----- Feed -----------------------------------------------------------------

export async function getAllSwapSecurities(
  getAccessTokenSilently: Function,
  forceReload = false,
): Promise<void> {
  const store = swapSecuritiesStore.getState();
  await loadReferenceDataOnce<ISwapSecurity>({
    url: SWAP_SECURITIES_ENDPOINT,
    source: "swapSecuritiesStoreACTIONS.getAllSwapSecurities",
    getAccessTokenSilently,
    readStatus: () => swapSecuritiesStore.getState().swapSecuritiesStatus,
    setStatus: store.setSwapSecuritiesStatus,
    setItems: store.setSwapSecurities,
    setError: store.setSwapSecuritiesError,
    extractItems: extractSwapSecurities,
    forceReload,
  });
}

// ----- Search / lookup ------------------------------------------------------

const returnWords = (value: string) => value.toLowerCase().split(/\s+/).filter(Boolean);

// Spec: tokens ("AUD 5Y OIS") narrow the list, each matching the START of a word so
// "5Y" does not find "15Y". Whole-word hits rank first, then the payload's order.
export function searchSwapSecurities(
  items: ISwapSecurity[],
  term: string,
  limit: number = DEFAULT_SEARCH_LIMIT,
): ISearchResult<ISwapSecurity> {
  const tokens = returnWords(term ?? "");
  if (tokens.length === 0) {
    return { items: items.slice(0, limit), totalMatches: items.length };
  }
  const ranked: { item: ISwapSecurity; exact: number; index: number }[] = [];
  items.forEach((item, index) => {
    const words = returnWords(item.securityName);
    let exact = 0;
    for (const token of tokens) {
      if (!words.some((word) => word.startsWith(token))) return;
      if (words.includes(token)) exact += 1;
    }
    ranked.push({ item, exact, index });
  });
  ranked.sort((a, b) => b.exact - a.exact || a.index - b.index);
  return {
    items: ranked.slice(0, limit).map((entry) => entry.item),
    totalMatches: ranked.length,
  };
}

export function findSwapSecurityByName(
  securityName: string,
): ISwapSecurity | undefined {
  return findItemByFieldValue<ISwapSecurity>(
    swapSecuritiesStore.getState().swapSecurities,
    SwapSecurityField.securityName,
    securityName,
  );
}
